import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from './ui/sheet';
import { useIsMobile } from './ui/use-mobile';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { 
  Users, 
  BookOpen, 
  Calendar, 
  MapPin, 
  GraduationCap,
  UserCog,
  Edit,
  Trash2,
  Archive,
  Bot,
  Menu,
  Home,
  FileText,
  TrendingUp,
  Search,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  UserCheck,
  Megaphone,
  Settings2,
  CalendarOff
} from 'lucide-react';
import { CourseManagement } from './admin/CourseManagement';
import { SubjectManagement } from './admin/SubjectManagement';
import { StudentManagement } from './admin/StudentManagement';
import { FacultyManagement } from './admin/FacultyManagement';
import { ScheduleManagement } from './admin/ScheduleManagement';
import { RoomManagement } from './admin/RoomManagement';
import { AdminManagement } from './admin/AdminManagement';
import { DocumentManagement } from './admin/DocumentManagement';
import { VerificationManagement } from './admin/VerificationManagement';
import { AnnouncementManagement } from './admin/AnnouncementManagement';
import { HolidayClassModeManagement } from './admin/HolidayClassModeManagement';
//import { DebugPanel } from './admin/DebugPanel';
//import { ConnectionStatusBanner } from './ConnectionStatusBanner';
import { User as UserType } from '../types';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentAcademicPeriod } from '../utils/academicYear';

interface AdminDashboardProps {
  user: UserType;
}

export function AdminDashboard({ user }: AdminDashboardProps) {
  const { accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCards, setExpandedCards] = useState<string[]>(['courses', 'subjects']);
  const [pendingCount, setPendingCount] = useState(0);

  // Global academic year & semester setting
  const currentYear = new Date().getFullYear();
  const academicYearOptions = [
    `${currentYear - 1}-${currentYear}`,
    `${currentYear}-${currentYear + 1}`,
    `${currentYear + 1}-${currentYear + 2}`,
    `${currentYear + 2}-${currentYear + 3}`,
  ];
  const { academicYear: defaultAY, semester: defaultSem } = getCurrentAcademicPeriod();
  const [globalAcademicYear, setGlobalAcademicYear] = useState(defaultAY);
  const [globalSemester, setGlobalSemester] = useState(defaultSem);
  const [stats, setStats] = useState([
    { 
      id: 'courses',
      title: 'Total Programs', 
      value: 0, 
      icon: BookOpen, 
      color: 'bg-blue-500',
      gradient: 'from-blue-400 to-blue-600',
      //trend: '+0%'
    },
    { 
      id: 'subjects',
      title: 'Total Courses', 
      value: 0, 
      icon: BookOpen, 
      color: 'bg-green-500',
      gradient: 'from-green-400 to-green-600',
      //trend: '+0%'
    },
    { 
      id: 'students',
      title: 'Total Students', 
      value: 0, 
      icon: Users, 
      color: 'bg-purple-500',
      gradient: 'from-purple-400 to-purple-600',
      //trend: '+0%'
    },
    { 
      id: 'faculty',
      title: 'Total Faculty', 
      value: 0, 
      icon: GraduationCap, 
      color: 'bg-orange-500',
      gradient: 'from-orange-400 to-orange-600',
      //trend: '+0%'
    },
    { 
      id: 'schedules',
      title: 'Total Schedules', 
      value: 0, 
      icon: Calendar, 
      color: 'bg-red-500',
      gradient: 'from-red-400 to-red-600',
      //trend: '+0%'
    },
    { 
      id: 'rooms',
      title: 'Total Rooms', 
      value: 0, 
      icon: MapPin, 
      color: 'bg-indigo-500',
      gradient: 'from-indigo-400 to-indigo-600',
      //trend: '+0%'
    }
  ]);
  const [archiveStats, setArchiveStats] = useState({
    total: 0,
    'faculty-workload': 0,
    'iso-forms': 0,
    'gs-manual': 0,
    'unit-manual': 0,
    'thesis-dissertation-forms': 0,
    other: 0
  });
  const isMobile = useIsMobile();

  const menuItems = [
    { value: 'overview', label: 'Overview', icon: Home, description: 'Dashboard home' },
    { value: 'courses', label: 'Programs', icon: BookOpen, description: 'Manage programs' },
    { value: 'subjects', label: 'Courses', icon: FileText, description: 'Manage courses' },
    { value: 'students', label: 'Students', icon: Users, description: 'Manage students' },
    { value: 'faculty', label: 'Faculty', icon: GraduationCap, description: 'Manage faculty' },
    { value: 'schedules', label: 'Schedules', icon: Calendar, description: 'Manage schedules' },
    { value: 'rooms', label: 'Rooms', icon: MapPin, description: 'Manage rooms' },
    { value: 'admins', label: 'Admins', icon: UserCog, description: 'Manage admins' },
    { value: 'verification', label: 'User Verification', icon: UserCheck, description: 'Verify new accounts' },
    { value: 'announcements', label: 'Bulletin Board', icon: Megaphone, description: 'Manage announcements' },
    { value: 'holidays', label: 'Holidays & Class Mode', icon: CalendarOff, description: 'Manage holidays & class mode' },
    { value: 'archive', label: 'Document Archive', icon: Archive, description: 'View documents' }
  ];

  const toggleCardExpanded = (id: string) => {
    setExpandedCards(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  // Function to fetch archive statistics from API
  const fetchArchiveStats = async () => {
    try {
      const token = accessToken || '';
      const response = await api.getArchiveStats(token);
      return response.stats;
    } catch (error) {
      console.error('Error fetching archive stats:', error);
      return {
        total: 0,
        'faculty-workload': 0,
        'iso-forms': 0,
        'gs-manual': 0,
        'unit-manual': 0,
        'thesis-dissertation-forms': 0,
        other: 0
      };
    }
  };

  const fetchPendingCount = async () => {
    if (!accessToken) return;
    try {
      const data = await api.getPendingVerifications(accessToken);
      setPendingCount(data.users?.length || 0);
    } catch (e) {
      console.error('Error fetching pending count:', e);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = accessToken || '';
        const [courses, subjects, students, faculty, schedules, rooms, archiveStatsData] = await Promise.all([
          api.getCourses(token).catch(() => ({ courses: [] })),
          api.getSubjects(token).catch(() => ({ subjects: [] })),
          api.getStudents(token).catch(() => ({ students: [] })),
          api.getFaculty(token).catch(() => ({ faculty: [] })),
          api.getSchedules(token).catch(() => ({ schedules: [] })),
          api.getRooms(token).catch(() => ({ rooms: [] })),
          fetchArchiveStats()
        ]);

        fetchPendingCount();

        setArchiveStats(archiveStatsData);

        setStats(prevStats => [
          { 
            ...prevStats[0],
            value: courses.courses.length, 
            trend: '+0%'
          },
          { 
            ...prevStats[1],
            value: subjects.subjects.length, 
            trend: '+0%'
          },
          { 
            ...prevStats[2],
            value: students.students.length, 
            trend: '+0%'
          },
          { 
            ...prevStats[3],
            value: faculty.faculty.length, 
            trend: '+0%'
          },
          { 
            ...prevStats[4],
            value: schedules.schedules.length, 
            trend: '+0%'
          },
          { 
            ...prevStats[5],
            value: rooms.rooms.length, 
            trend: '+0%'
          }
        ]);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();

    // Set up custom event listener for document archive changes
    const handleArchiveChange = async () => {
      console.log('Document archive change detected');
      const archiveStatsData = await fetchArchiveStats();
      setArchiveStats(archiveStatsData);
    };

    const handleVerificationChange = () => {
      fetchPendingCount();
    };

    window.addEventListener('archiveChanged', handleArchiveChange);
    window.addEventListener('verificationChanged', handleVerificationChange);

    return () => {
      window.removeEventListener('archiveChanged', handleArchiveChange);
      window.removeEventListener('verificationChanged', handleVerificationChange);
    };
  }, [accessToken]);

  const handleMenuItemClick = (value: string) => {
    setActiveTab(value);
    setIsMenuOpen(false);
  };

  const getCurrentPageTitle = () => {
    const item = menuItems.find(item => item.value === activeTab);
    return item ? item.label : 'Overview';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex" role="region" aria-label="Administrator Dashboard">

      {/* ───── Desktop Sidebar (aside) ───── */}
      <aside className="hidden md:flex md:flex-col md:w-64 lg:w-72 flex-shrink-0 border-r border-slate-200/80 bg-white/80 backdrop-blur-xl sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto shadow-sm z-30">
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
            <Settings2 className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-extrabold tracking-tight bg-gradient-to-r from-blue-700 to-indigo-600 bg-clip-text text-transparent leading-tight">
              Admin Panel
            </h1>
            <p className="text-[11px] text-muted-foreground leading-tight truncate">
              {user.department || 'Administration'}
            </p>
          </div>
        </div>

        {/* Navigation items */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav aria-label="Admin sidebar navigation" className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.value;
              return (
                <button
                  key={item.value}
                  onClick={() => setActiveTab(item.value)}
                  className={`
                    w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 group relative
                    ${isActive
                      ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm ring-1 ring-blue-100'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full bg-blue-600" />
                  )}
                  <Icon className={`h-[18px] w-[18px] flex-shrink-0 transition-colors ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                  <span className="truncate">{item.label}</span>
                  {item.value === 'verification' && pendingCount > 0 && (
                    <Badge variant="destructive" className="ml-auto h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold">
                      {pendingCount}
                    </Badge>
                  )}
                </button>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Sidebar footer — Academic period & status */}
        <div className="border-t border-slate-100 px-4 py-4 space-y-3">
          <div className="space-y-2">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Academic Period</Label>
            <Select value={globalSemester.toString()} onValueChange={(v) => setGlobalSemester(parseInt(v))}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1st Semester</SelectItem>
                <SelectItem value="2">2nd Semester</SelectItem>
                <SelectItem value="3">Summer</SelectItem>
              </SelectContent>
            </Select>
            <Select value={globalAcademicYear} onValueChange={setGlobalAcademicYear}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {academicYearOptions.map((year) => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="px-2 py-0.5 text-[10px]">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Active
            </Badge>
            <span className="text-[11px] text-muted-foreground truncate">{user.name.split(' ')[0]}</span>
          </div>
        </div>
      </aside>

      {/* ───── Mobile Sidebar (Sheet) ───── */}
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent side="left" className="w-[280px] sm:w-[320px] p-0 flex flex-col">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md">
                <Settings2 className="h-4 w-4 text-white" />
              </div>
              <div>
                <SheetTitle className="text-sm font-extrabold tracking-tight">Admin Panel</SheetTitle>
                <SheetDescription className="text-[11px]">Navigate admin sections</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1 px-3 py-3">
            <nav aria-label="Mobile admin navigation" className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.value;
                return (
                  <button
                    key={item.value}
                    onClick={() => handleMenuItemClick(item.value)}
                    className={`
                      w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 group relative
                      ${isActive
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm ring-1 ring-blue-100'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }
                    `}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full bg-blue-600" />
                    )}
                    <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                    <span className="truncate">{item.label}</span>
                    {item.value === 'verification' && pendingCount > 0 && (
                      <Badge variant="destructive" className="ml-auto h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold">
                        {pendingCount}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Mobile sidebar footer */}
          <div className="border-t border-slate-100 px-4 py-3 space-y-2">
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Academic Period</Label>
            <Select value={globalSemester.toString()} onValueChange={(v) => setGlobalSemester(parseInt(v))}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1st Semester</SelectItem>
                <SelectItem value="2">2nd Semester</SelectItem>
                <SelectItem value="3">Summer</SelectItem>
              </SelectContent>
            </Select>
            <Select value={globalAcademicYear} onValueChange={setGlobalAcademicYear}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {academicYearOptions.map((year) => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </SheetContent>
      </Sheet>

      {/* ───── Main Content Area ───── */}
      <main className="flex-1 min-w-0 w-full flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-16 z-20 bg-white/80 backdrop-blur-lg border-b border-slate-200/60 px-4 md:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* Mobile hamburger */}
              <Button
                variant="outline"
                size="icon"
                className="md:hidden h-9 w-9 flex-shrink-0 border-slate-200"
                onClick={() => setIsMenuOpen(true)}
                aria-label="Open navigation menu"
              >
                <Menu className="h-4 w-4" />
              </Button>

              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight text-slate-900 truncate">
                  {getCurrentPageTitle()}
                </h2>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                  Welcome back, <span className="font-semibold text-slate-700">{user.name}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge variant="secondary" className="hidden sm:flex px-2.5 py-1 text-xs">
                <CheckCircle2 className="h-3 w-3 mr-1.5" />
                Active
              </Badge>
              <Badge variant="outline" className="px-2.5 py-1 text-xs">
                {user.department || 'Admin'}
              </Badge>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 w-full p-4 md:p-6 lg:p-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
            {/* Hidden TabsList for accessibility — navigation is handled by sidebar */}
            <TabsList className="sr-only">
              {menuItems.map((item) => (
                <TabsTrigger key={item.value} value={item.value}>{item.label}</TabsTrigger>
              ))}
            </TabsList>

            {/* Overview Tab - Enhanced Dashboard */}
            <TabsContent value="overview" className="w-full space-y-8 mt-0">
              {/* Stats Grid - Interactive Cards */}
              <section aria-label="System statistics">
                <h3 className="text-base sm:text-lg md:text-xl font-bold tracking-tight text-slate-800 mb-5 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  System Statistics
                </h3>
                <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full">
                  {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <Card
                        key={index}
                        className="cursor-pointer hover:shadow-xl transition-all duration-300 group hover:-translate-y-1 overflow-hidden relative border-slate-200/80 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
                        onClick={() => setActiveTab(stat.id)}
                        tabIndex={0}
                        role="button"
                        aria-label={`View ${stat.title}: ${stat.value}`}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(stat.id); } }}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-300`} />
                        <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${stat.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3 relative z-10">
                          <div className="space-y-1.5 flex-1">
                            <CardTitle className="text-xs sm:text-sm font-semibold text-muted-foreground tracking-wide uppercase">
                              {stat.title}
                            </CardTitle>
                            <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
                              {stat.value}
                            </div>
                          </div>
                          <div className={`rounded-xl p-3 text-white bg-gradient-to-br ${stat.gradient} group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                            <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs w-full justify-center gap-1 text-muted-foreground group-hover:bg-secondary/80 group-hover:text-foreground"
                            onClick={() => setActiveTab(stat.id)}
                          >
                            View Details
                            <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            </TabsContent>

            {/* Other Tab Contents */}
            <TabsContent value="courses" className="mt-0">
              <CourseManagement />
            </TabsContent>

            <TabsContent value="subjects" className="mt-0">
              <SubjectManagement globalSemester={globalSemester} globalAcademicYear={globalAcademicYear} />
            </TabsContent>

            <TabsContent value="students" className="mt-0">
              <StudentManagement />
            </TabsContent>

            <TabsContent value="faculty" className="mt-0">
              <FacultyManagement />
            </TabsContent>

            <TabsContent value="schedules" className="mt-0">
              <ScheduleManagement globalSemester={globalSemester} globalAcademicYear={globalAcademicYear} />
            </TabsContent>

            <TabsContent value="rooms" className="mt-0">
              <RoomManagement />
            </TabsContent>

            <TabsContent value="admins" className="mt-0">
              <AdminManagement />
            </TabsContent>

            <TabsContent value="verification" className="mt-0">
              <VerificationManagement />
            </TabsContent>

            <TabsContent value="announcements" className="mt-0">
              <AnnouncementManagement globalSemester={globalSemester} globalAcademicYear={globalAcademicYear} />
            </TabsContent>

            <TabsContent value="holidays" className="mt-0">
              <HolidayClassModeManagement globalSemester={globalSemester} globalAcademicYear={globalAcademicYear} />
            </TabsContent>

            <TabsContent value="archive" className="mt-0">
              <DocumentManagement currentUserId={user.id} currentUserName={user.name} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}